// ModalAgregarClientesAsociados.tsx — Co Marca
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, UserPlus, AlertCircle, Check, Users, Phone, CreditCard, User } from 'lucide-react';
import { Cliente } from '../../services/citasService';

interface Props {
    isOpen: boolean; onClose: () => void;
    onAgregar: (ids: number[]) => void;
    clientesExcluir: number[]; clientes: Cliente[];
    capacidadMaxima?: number;
}

const C = {
    brand:        '#61257b',   // Púrpura primario (Co Marca)
    brandDark:    '#3d1250',   // Púrpura oscuro
    brandLight:   '#8b3fa8',   // Púrpura suave
    accent1:      '#c1348b',   // Magenta / Acento principal
    accent1Dark:  '#8f1f64',   // Magenta oscuro
    accent2:      '#ea8406',   // Naranja cálido
    accent2Dark:  '#b86300',   // Naranja oscuro
    accent3:      '#ffd97c',   // Amarillo dorado
    accent3Dark:  '#f0b429',   // Dorado intenso
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
.maca-modal *{font-family:'Plus Jakarta Sans',sans-serif;box-sizing:border-box;}
@keyframes macaFadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes macaSlideUp{from{opacity:0;transform:translateY(20px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);}}
.maca-overlay{animation:macaFadeIn .22s ease both;}
.maca-dialog{animation:macaSlideUp .3s cubic-bezier(.16,1,.3,1) both;}
.maca-search{transition:all .2s;}
.maca-search:focus{outline:none;border-color:${C.brand};box-shadow:0 0 0 3px rgba(97,37,123,.12);background:#fff;}
.maca-client-row{border-radius:14px;border:1.5px solid #f1f5f9;padding:10px 14px;cursor:pointer;transition:all .2s cubic-bezier(.16,1,.3,1);display:flex;align-items:center;gap:12px;background:#fff;}
.maca-client-row:hover:not(.disabled){border-color:${C.brand}40;background:${C.brand}05;transform:translateY(-1px);}
.maca-client-row.selected{border-color:${C.brand};background:${C.brand}0c;box-shadow:0 4px 14px rgba(97,37,123,.08);}
.maca-client-row.disabled{opacity:.45;cursor:not-allowed;}
.maca-btn-primary{display:inline-flex;align-items:center;gap:7px;padding:10px 22px;border-radius:11px;border:none;background:linear-gradient(135deg,${C.brand},${C.accent1});color:#fff;font-weight:800;font-size:.88rem;cursor:pointer;box-shadow:0 4px 14px rgba(97,37,123,.28);transition:all .22s cubic-bezier(.16,1,.3,1);}
.maca-btn-primary:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 8px 22px rgba(97,37,123,.38);}
.maca-btn-primary:disabled{background:#e2e8f0;color:#94a3b8;box-shadow:none;cursor:not-allowed;}
.maca-btn-ghost{display:inline-flex;align-items:center;gap:6px;padding:10px 18px;border-radius:11px;border:1.5px solid #e2e8f0;background:#f8fafc;color:#374151;font-weight:700;font-size:.88rem;cursor:pointer;transition:all .2s;}
.maca-btn-ghost:hover{background:#f1f5f9;}
.maca-scroll::-webkit-scrollbar{width:5px;}
.maca-scroll::-webkit-scrollbar-track{background:transparent;}
.maca-scroll::-webkit-scrollbar-thumb{background:rgba(97,37,123,.2);border-radius:10px;}
`;

const getInitials = (firstName?: string, lastName?: string) => {
    const f = (firstName || '').trim().charAt(0).toUpperCase();
    const l = (lastName || '').trim().charAt(0).toUpperCase();
    return (f + l) || 'C';
};

const ModalAgregarClientesAsociados: React.FC<Props> = ({ isOpen, onClose, onAgregar, clientesExcluir, clientes, capacidadMaxima }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    if (!isOpen) return null;

    const excedeCapacidad = capacidadMaxima !== undefined && selectedIds.length > capacidadMaxima;
    const capacidadRestante = capacidadMaxima !== undefined ? Math.max(0, capacidadMaxima - selectedIds.length) : Infinity;

    const filteredClients = clientes.filter(c => {
        const s = searchTerm.toLowerCase();
        return !!c.usuario && (c.usuario?.first_name?.toLowerCase().includes(s) || c.usuario?.last_name?.toLowerCase().includes(s) || c.ci_nit?.toLowerCase().includes(s) || c.telefono?.toLowerCase().includes(s)) && !clientesExcluir.includes(c.id);
    });

    const toggle = (id: number) => {
        if (selectedIds.includes(id)) { setSelectedIds(p=>p.filter(x=>x!==id)); return; }
        if (capacidadMaxima !== undefined && selectedIds.length >= capacidadMaxima) { alert(`Máximo ${capacidadMaxima} clientes asociados.`); return; }
        setSelectedIds(p=>[...p,id]);
    };

    const handleAgregar = () => {
        if (!selectedIds.length || excedeCapacidad) return;
        onAgregar(selectedIds); setSelectedIds([]); setSearchTerm('');
    };

    const handleClose = () => { setSelectedIds([]); setSearchTerm(''); onClose(); };

    return createPortal(
        <>
            <style>{css}</style>
            <div className="maca-modal maca-overlay" style={{position:'fixed',inset:0,zIndex:99999,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px',background:'rgba(15,23,42,.55)',backdropFilter:'blur(6px)'}}>
                <div className="maca-dialog" onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:'22px',width:'100%',maxWidth:'580px',maxHeight:'88vh',display:'flex',flexDirection:'column',boxShadow:'0 32px 80px rgba(0,0,0,.2)',border:'1.5px solid #f1f5f9',overflow:'hidden'}}>

                    {/* Top bar */}
                    <div style={{height:'4px',background:`linear-gradient(90deg,${C.brand},${C.accent1},${C.accent2},${C.accent3})`,flexShrink:0}}/>

                    {/* Header */}
                    <div style={{padding:'18px 22px',borderBottom:'1.5px solid #f1f5f9',display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexShrink:0}}>
                        <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                            <div style={{width:'38px',height:'38px',borderRadius:'12px',background:`linear-gradient(135deg,${C.brand},${C.accent1})`,display:'flex',alignItems:'center',justifyContent:'center',boxShadow:`0 4px 14px rgba(97,37,123,.25)`}}>
                                <UserPlus size={18} color="#fff"/>
                            </div>
                            <div>
                                <h3 style={{fontSize:'1rem',fontWeight:'900',color:'#0f172a',letterSpacing:'-.02em'}}>Agregar Clientes Asociados</h3>
                                <p style={{fontSize:'.72rem',color:'#94a3b8',marginTop:'2px'}}>Selecciona clientes adicionales para esta cita</p>
                            </div>
                        </div>
                        <button onClick={handleClose} style={{width:'30px',height:'30px',borderRadius:'9px',border:'1.5px solid #f1f5f9',background:'#f8fafc',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='#f8fafc'}>
                            <X size={14} color="#64748b"/>
                        </button>
                    </div>

                    {/* Body */}
                    <div className="maca-scroll" style={{flex:1,overflowY:'auto',padding:'16px 22px',display:'flex',flexDirection:'column',gap:'12px'}}>

                        {/* Capacidad */}
                        {capacidadMaxima !== undefined && (
                            <div style={{padding:'11px 14px',borderRadius:'12px',background: excedeCapacidad?`${C.accent1}0c`:`${C.brand}08`,border:`1.5px solid ${excedeCapacidad?C.accent1+'30':C.brand+'20'}`,display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px'}}>
                                <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                                    <Users size={15} color={excedeCapacidad?C.accent1:C.brand}/>
                                    <div>
                                        <p style={{fontSize:'.78rem',fontWeight:'800',color:excedeCapacidad?C.accent1Dark:C.brand}}>Capacidad: máx. {capacidadMaxima} asociados</p>
                                        <p style={{fontSize:'.68rem',color:'#64748b',marginTop:'1px'}}>{capacidadRestante===0?'Límite alcanzado':`Puedes agregar ${capacidadRestante} cliente(s) más`}</p>
                                    </div>
                                </div>
                                {excedeCapacidad&&<span style={{display:'inline-flex',alignItems:'center',gap:'4px',padding:'3px 8px',borderRadius:'6px',background:`${C.accent1}15`,color:C.accent1Dark,fontSize:'.68rem',fontWeight:'800'}}><AlertCircle size={10}/> Excede</span>}
                            </div>
                        )}

                        {/* Búsqueda */}
                        <div style={{position:'relative'}}>
                            <Search size={14} color="#94a3b8" style={{position:'absolute',left:'12px',top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}/>
                            <input type="text" placeholder="Buscar por nombre, CI/NIT o teléfono..." value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} className="maca-search"
                                style={{width:'100%',padding:'9px 34px 9px 34px',borderRadius:'10px',border:'1.5px solid #e5e7eb',background:'#fafafa',fontSize:'.84rem',color:'#1e293b'}}/>
                            {searchTerm && (
                                <button type="button" onClick={()=>setSearchTerm('')} style={{position:'absolute',right:'10px',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',padding:'3px'}}>
                                    <X size={13} color="#94a3b8"/>
                                </button>
                            )}
                        </div>

                        {/* Lista */}
                        {filteredClients.length===0?(
                            <div style={{textAlign:'center',padding:'32px 16px'}}>
                                <div style={{width:'48px',height:'48px',borderRadius:'14px',background:`${C.brand}0a`,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 10px'}}>
                                    <UserPlus size={24} color={C.brand} style={{opacity:.6}}/>
                                </div>
                                <p style={{fontSize:'.85rem',color:'#475569',fontWeight:'700'}}>No se encontraron clientes disponibles</p>
                                <p style={{fontSize:'.72rem',color:'#94a3b8',marginTop:'4px'}}>{searchTerm?'Prueba otro término de búsqueda':'Todos los clientes registrados ya están incluidos'}</p>
                            </div>
                        ):(
                            <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                                {filteredClients.map(c=>{
                                    const isSel=selectedIds.includes(c.id);
                                    const isDisabled=!isSel&&capacidadMaxima!==undefined&&selectedIds.length>=capacidadMaxima;
                                    const fullName = c.usuario ? `${c.usuario.first_name} ${c.usuario.last_name}` : `Cliente #${c.id}`;
                                    const initials = getInitials(c.usuario?.first_name, c.usuario?.last_name);

                                    return(
                                        <div key={c.id} className={`maca-client-row${isSel?' selected':''}${isDisabled?' disabled':''}`} onClick={()=>!isDisabled&&toggle(c.id)}>
                                            {/* Checkbox */}
                                            <div style={{width:'20px',height:'20px',borderRadius:'6px',border:`2px solid ${isSel?C.brand:isDisabled?'#d1d5db':'#cbd5e1'}`,background:isSel?`linear-gradient(135deg,${C.brand},${C.accent1})`:'#fff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'all .2s',boxShadow:isSel?'0 2px 6px rgba(97,37,123,.3)':undefined}}>
                                                {isSel&&<Check size={12} color="#fff" strokeWidth={3}/>}
                                            </div>

                                            {/* Avatar Initials */}
                                            <div style={{width:'34px',height:'34px',borderRadius:'10px',background:isSel?`${C.brand}18`:'#f1f5f9',color:isSel?C.brand:'#475569',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:'800',fontSize:'.75rem',flexShrink:0}}>
                                                {initials}
                                            </div>

                                            <div style={{flex:1,minWidth:0}}>
                                                <p style={{fontSize:'.85rem',fontWeight:'800',color:isSel?C.brandDark:'#0f172a',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                                                    {fullName}
                                                </p>
                                                <div style={{display:'flex',gap:'10px',marginTop:'3px',flexWrap:'wrap',alignItems:'center'}}>
                                                    {c.ci_nit&&(
                                                        <span style={{display:'inline-flex',alignItems:'center',gap:'3px',fontSize:'.68rem',color:'#64748b',fontWeight:'600'}}>
                                                            <CreditCard size={10} color={C.brand}/> {c.ci_nit}
                                                        </span>
                                                    )}
                                                    {c.telefono&&(
                                                        <span style={{display:'inline-flex',alignItems:'center',gap:'3px',fontSize:'.68rem',color:'#64748b',fontWeight:'600'}}>
                                                            <Phone size={10} color={C.accent2Dark}/> {c.telefono}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {isDisabled&&!isSel&&<span style={{fontSize:'.65rem',color:'#94a3b8',fontWeight:'700',flexShrink:0,background:'#f1f5f9',padding:'3px 7px',borderRadius:'6px'}}>Límite</span>}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div style={{padding:'14px 22px',borderTop:'1.5px solid #f1f5f9',background:'#fafafa',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px'}}>
                        <div>
                            <p style={{fontSize:'.82rem',fontWeight:'700',color:'#374151'}}>
                                Seleccionados: <strong style={{color:C.brand,fontWeight:'900',fontSize:'.95rem'}}>{selectedIds.length}</strong>
                            </p>
                            {capacidadMaxima!==undefined&&(
                                <p style={{fontSize:'.68rem',fontWeight:'600',color:excedeCapacidad?C.accent1Dark:'#64748b',marginTop:'1px'}}>
                                    {excedeCapacidad?`¡Excede el máximo! (Máx: ${capacidadMaxima})`:`Dentro del límite (Máx: ${capacidadMaxima})`}
                                </p>
                            )}
                        </div>
                        <div style={{display:'flex',gap:'8px'}}>
                            <button onClick={handleClose} className="maca-btn-ghost">Cancelar</button>
                            <button onClick={handleAgregar} disabled={!selectedIds.length||excedeCapacidad} className="maca-btn-primary">
                                <UserPlus size={15}/>
                                {excedeCapacidad?`Excede (${selectedIds.length}/${capacidadMaxima})`:`Agregar (${selectedIds.length})`}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default ModalAgregarClientesAsociados;
